import { createBrowserRouter } from "react-router";
import RootLayout from "./components/RootLayout";
import SiteLayout from "./components/SiteLayout";
import FeatureGate from "./components/FeatureGate";
import HomePage from "./components/pages/HomePage";
import AboutPage from "./components/pages/AboutPage";
import { CardDesignLab } from "./routes/CardDesignLab";
import { Login } from "./routes/Login";
import { ForgotPassword } from "./routes/ForgotPassword";
import { ResetPassword } from "./routes/ResetPassword";
import { Account } from "./routes/Account";
import { Feed } from "./routes/Feed";
import AdminShell from "./components/admin/AdminShell";
import AdminDashboard from "./routes/admin/AdminDashboard";
import AdminProducts from "./routes/admin/AdminProducts";
import AdminOrders from "./routes/admin/AdminOrders";
import AdminSubscribers from "./routes/admin/AdminSubscribers";
import AdminCategories from "./routes/admin/AdminCategories";
import AdminCoupons from "./routes/admin/AdminCoupons";
import AdminAffiliateLinks from "./routes/admin/AdminAffiliateLinks";
import AdminFavoriteLinks from "./routes/admin/AdminFavoriteLinks";
import AdminWaitlists from "./routes/admin/AdminWaitlists";
import AdminGuideTopics from "./routes/admin/AdminGuideTopics";
import AdminMedia from "./routes/admin/AdminMedia";
import AdminRegistrations from "./routes/admin/AdminRegistrations";
import AdminSales from "./routes/admin/AdminSales";
import AdminGuides from "./routes/admin/AdminGuides";
import AdminVideos from "./routes/admin/AdminVideos";
import AdminRetreats from "./routes/admin/AdminRetreats";
import AdminSettings from "./routes/admin/AdminSettings";
import AdminStaff from "./routes/admin/AdminStaff";
import { Subscribe } from "./routes/Subscribe";
import { Billing } from "./routes/Billing";
import { Community } from "./routes/Community";
import { Shop } from "./routes/Shop";
import { ProductDetail } from "./routes/ProductDetail";
import { Retreats, retreatsLoader } from "./routes/Retreats";
import { RetreatDetail, retreatDetailLoader } from "./routes/RetreatDetail";
import { RetreatCardDesignLab, retreatCardDesignLabLoader } from "./routes/RetreatCardDesignLab";
import { Favorites } from "./routes/Favorites";
import { Guides, guidesLoader } from "./routes/Guides";
import { Videos } from "./routes/Videos";
import { GuideTopic } from "./routes/GuideTopic";
import { GuideDetail } from "./routes/GuideDetail";
import { NotFound, RouteError } from "./routes/NotFound";
import { LooseThreadsClub } from "./routes/LooseThreadsClub";
import { SimplePage } from "./routes/SimplePage";
import { HeroIterations } from "./routes/HeroIterations";
import { ClubPreview } from "./routes/ClubPreview";
import { HomeBlue } from "./routes/HomeBlue";
import { HomePattern } from "./routes/HomePattern";
import { ClubBlue } from "./routes/ClubBlue";
import { HomePattern2 } from "./routes/HomePattern2";
import { HomeBlue2 } from "./routes/HomeBlue2";
import { ClubBlue2 } from "./routes/ClubBlue2";
import { EventStripLab } from "./routes/EventStripLab";
import { EventCardLab } from "./routes/EventCardLab";
import { Blue2Links } from "./routes/blue2/pages/Links";
import { Blue2About } from "./routes/blue2/pages/About";
import { Blue2Favorites } from "./routes/blue2/pages/Favorites";
import { Blue2Videos } from "./routes/blue2/pages/Videos";
import { Blue2Retreats } from "./routes/blue2/pages/Retreats";
import { Blue2Shop } from "./routes/blue2/pages/Shop";
import { Blue2Product } from "./routes/blue2/pages/Product";
import { Blue2Retreat } from "./routes/blue2/pages/Retreat";
import { Blue2Event } from "./routes/blue2/pages/Event";
import { Blue2NotFound } from "./routes/blue2/pages/NotFound";
import { MembersHome } from "./routes/blue2/members/Home";
import { MembersPosts } from "./routes/blue2/members/Posts";
import { MembersPost } from "./routes/blue2/members/Post";
import { MembersClasses } from "./routes/blue2/members/Classes";
import { MembersKlass } from "./routes/blue2/members/Klass";
import { MembersLive } from "./routes/blue2/members/Live";
import { MembersCommunity } from "./routes/blue2/members/Community";
import { MembersAccount } from "./routes/blue2/members/Account";
import { MembersJoin } from "./routes/blue2/members/Join";
import { PortalHome } from "./routes/blue2/portal/Home";
import { PortalClasses } from "./routes/blue2/portal/Classes";
import { PortalProject } from "./routes/blue2/portal/Project";
import { PortalSession } from "./routes/blue2/portal/Session";
import { PortalCommunity } from "./routes/blue2/portal/Community";
import { PortalMe } from "./routes/blue2/portal/Me";
import { PortalJoin } from "./routes/blue2/portal/Join";
import { ClubPattern } from "./routes/ClubPattern";
import { StitchesNightOut } from "./routes/StitchesNightOut";
import { SOCIALS } from "./lib/social";
import {
  ShopSuccess,
  ShopCancel,
  RetreatSuccess,
  RetreatCancel,
} from "./routes/CheckoutResult";

export const router = createBrowserRouter([
  {
    Component: RootLayout,
    errorElement: <RouteError />,
    // Shown on first paint while a route loader resolves (e.g. landing directly
    // on /guides). Cream so it matches the site and never flashes white.
    HydrateFallback: () => <div className="min-h-dvh" style={{ backgroundColor: "#fff8ed" }} />,
    children: [
      // auth + portal — no site navbar, no page fade (own chrome)
      { path: "/login", Component: Login },
      { path: "/forgot-password", Component: ForgotPassword },
      { path: "/reset-password", Component: ResetPassword },
      { path: "/feed", Component: Feed },
      { path: "/account", Component: Account },
      { path: "/account/subscribe", Component: Subscribe },
      { path: "/account/billing", Component: Billing },
      { path: "/account/community", Component: Community },

      // unlisted homepage concepts for team review (noindex in _headers); own nav, no site navbar
      { path: "/home-preview/blue", Component: HomeBlue },
      { path: "/home-preview/pattern", Component: HomePattern },
      { path: "/home-preview/pattern-2", Component: HomePattern2 },
      { path: "/home-preview/blue-2", Component: HomeBlue2 },
      { path: "/club-preview/blue", Component: ClubBlue },
      { path: "/club-preview/blue-2", Component: ClubBlue2 },
      { path: "/home-preview/event-bars", Component: EventStripLab },
      { path: "/home-preview/event-cards", Component: EventCardLab },
      // blue-2 inner pages (noindex in _headers)
      { path: "/blue-2/links", Component: Blue2Links },
      { path: "/blue-2/about", Component: Blue2About },
      { path: "/blue-2/favorites", Component: Blue2Favorites },
      { path: "/blue-2/videos", Component: Blue2Videos },
      { path: "/blue-2/retreats", Component: Blue2Retreats, loader: retreatsLoader },
      { path: "/blue-2/retreats/:slug", Component: Blue2Retreat, loader: retreatDetailLoader },
      { path: "/blue-2/shop", Component: Blue2Shop },
      { path: "/blue-2/shop/:slug", Component: Blue2Product },
      { path: "/blue-2/events/stitches-night-out", Component: Blue2Event },
      // in-house club prototype (sample data)
      { path: "/blue-2/members", Component: MembersHome },
      { path: "/blue-2/members/posts", Component: MembersPosts },
      { path: "/blue-2/members/posts/:id", Component: MembersPost },
      { path: "/blue-2/members/classes", Component: MembersClasses },
      { path: "/blue-2/members/classes/:id", Component: MembersKlass },
      { path: "/blue-2/members/live", Component: MembersLive },
      { path: "/blue-2/members/community", Component: MembersCommunity },
      { path: "/blue-2/members/account", Component: MembersAccount },
      { path: "/blue-2/members/join", Component: MembersJoin },
      // club portal v2 prototype (research-led: bottom tabs, project rooms, class screen)
      { path: "/blue-2/portal", Component: PortalHome },
      { path: "/blue-2/portal/classes", Component: PortalClasses },
      { path: "/blue-2/portal/projects/:id", Component: PortalProject },
      { path: "/blue-2/portal/session/:id", Component: PortalSession },
      { path: "/blue-2/portal/community", Component: PortalCommunity },
      { path: "/blue-2/portal/me", Component: PortalMe },
      { path: "/blue-2/portal/join", Component: PortalJoin },
      { path: "/blue-2/*", Component: Blue2NotFound },
      { path: "/club-preview/pattern", Component: ClubPattern },

      // admin (React) — replaces the server-rendered /admin
      {
        path: "/admin",
        Component: AdminShell,
        children: [
          { index: true, Component: AdminDashboard },
          { path: "products", Component: AdminProducts },
          { path: "orders", Component: AdminOrders },
          { path: "subscribers", Component: AdminSubscribers },
          { path: "categories", Component: AdminCategories },
          { path: "coupons", Component: AdminCoupons },
          { path: "affiliate-links", Component: AdminAffiliateLinks },
          { path: "favorite-links", Component: AdminFavoriteLinks },
          { path: "waitlists", Component: AdminWaitlists },
          { path: "guide-topics", Component: AdminGuideTopics },
          { path: "media", Component: AdminMedia },
          { path: "registrations", Component: AdminRegistrations },
          { path: "sales", Component: AdminSales },
          { path: "guides", Component: AdminGuides },
          { path: "videos", Component: AdminVideos },
          { path: "retreats", Component: AdminRetreats },
          { path: "settings", Component: AdminSettings },
          { path: "staff", Component: AdminStaff },
        ],
      },

      // public site — persistent navbar + page crossfade
      {
        Component: SiteLayout,
        children: [
          { path: "/", Component: HomePage },
          { path: "/events/stitches-night-out", Component: StitchesNightOut },

          // shop — gated on the `shop` feature flag (Admin → Settings)
          { path: "/shop", element: <FeatureGate flag="shop"><Shop /></FeatureGate> },
          { path: "/shop/:slug", element: <FeatureGate flag="shop"><ProductDetail /></FeatureGate> },
          { path: "/shop/success", element: <FeatureGate flag="shop"><ShopSuccess /></FeatureGate> },
          { path: "/shop/cancel", element: <FeatureGate flag="shop"><ShopCancel /></FeatureGate> },

          // retreats
          { path: "/retreats", Component: Retreats, loader: retreatsLoader },
          { path: "/retreats/card-design-lab", Component: RetreatCardDesignLab, loader: retreatCardDesignLabLoader },
          { path: "/retreats/:slug", Component: RetreatDetail, loader: retreatDetailLoader },
          { path: "/retreats/success", Component: RetreatSuccess },
          { path: "/retreats/cancel", Component: RetreatCancel },

          // favorites
          { path: "/favorites", Component: Favorites },
          { path: "/favorites/card-design-lab", Component: CardDesignLab },

          // videos
          { path: "/videos", Component: Videos },

          // guides — gated on the `guides` feature flag (Admin → Settings)
          {
            path: "/guides",
            element: <FeatureGate flag="guides"><Guides /></FeatureGate>,
            loader: guidesLoader,
          },
          { path: "/guides/:topicSlug", element: <FeatureGate flag="guides"><GuideTopic /></FeatureGate> },
          {
            path: "/guides/:topicSlug/:guideSlug",
            element: <FeatureGate flag="guides"><GuideDetail /></FeatureGate>,
          },

          // simple content pages
          { path: "/about", Component: AboutPage },
          { path: "/loose-threads-club", Component: LooseThreadsClub },
          { path: "/club-preview", Component: ClubPreview },
          {
            path: "/partnerships",
            element: (
              <SimplePage
                title="Partnerships"
                body={<>Interested in a brand collaboration with Chelsea? Reach out through <a href={SOCIALS.instagram.url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">@chelsea_b_porter on Instagram</a> to start the conversation.</>}
              />
            ),
          },

          // internal design iteration sandboxes
          { path: "/_design/hero", Component: HeroIterations },
          { path: "/_design/club", Component: ClubPreview },

          // catch-all 404
          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
]);
